import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import type { ConsentChoices } from "@/lib/consent";

interface ConsentCheckboxesProps {
  value: ConsentChoices;
  onChange: (value: ConsentChoices) => void;
  /** `dark` on the auth pages' #030712 panel, `light` on cards and dialogs. */
  tone?: "dark" | "light";
  /** Hide the marketing box (e.g. accepting an updated Terms version). */
  showMarketing?: boolean;
  /** Makes ids unique when two instances could mount at once. */
  idPrefix?: string;
  disabled?: boolean;
}

/**
 * The sign-up consent boxes (docs/specs/kyc-aml-profile.md): Terms + Privacy
 * is required; marketing is optional and unticked by default. The caller
 * records both choices through `recordConsent` once the user is known.
 */
export function ConsentCheckboxes({
  value,
  onChange,
  tone = "light",
  showMarketing = true,
  idPrefix = "consent",
  disabled = false,
}: ConsentCheckboxesProps) {
  const { t } = useLanguage();
  const text = tone === "dark" ? "text-white/70" : "text-foreground/80";
  const muted = tone === "dark" ? "text-white/50" : "text-muted-foreground";
  const link = cn("underline underline-offset-2", tone === "dark" ? "text-white" : "text-primary");
  const box = cn("mt-0.5 shrink-0", tone === "dark" && "border-white/60");

  return (
    <div className="flex w-full flex-col gap-3 text-start">
      <div className="flex items-start gap-2">
        <Checkbox
          id={`${idPrefix}-terms`}
          checked={value.terms}
          onCheckedChange={(v) => onChange({ ...value, terms: v === true })}
          className={box}
          aria-required="true"
          disabled={disabled}
        />
        <label htmlFor={`${idPrefix}-terms`} className={cn("cursor-pointer text-xs leading-relaxed", text)}>
          {t("أوافق على ", "I agree to the ")}
          <Link to="/terms" target="_blank" className={link}>
            {t("الشروط والأحكام", "Terms & Conditions")}
          </Link>
          {t(" وأقرّ بأنني اطلعت على ", " and confirm I have read the ")}
          <Link to="/privacy-policy" target="_blank" className={link}>
            {t("سياسة الخصوصية", "Privacy Policy")}
          </Link>
          .
        </label>
      </div>

      {showMarketing && (
        <div className="flex items-start gap-2">
          <Checkbox
            id={`${idPrefix}-marketing`}
            checked={value.marketing}
            onCheckedChange={(v) => onChange({ ...value, marketing: v === true })}
            className={box}
            disabled={disabled}
          />
          <label htmlFor={`${idPrefix}-marketing`} className={cn("cursor-pointer text-xs leading-relaxed", muted)}>
            {t(
              "أوافق على تلقي رسائل تسويقية وتوعوية من دلّالي عبر الجوال والبريد وواتساب، ويمكنني الإلغاء في أي وقت.",
              "I agree to receive marketing and awareness messages from Dallali by mobile, email and WhatsApp. I can unsubscribe at any time.",
            )}{" "}
            {t("(اختياري)", "(optional)")}
          </label>
        </div>
      )}
    </div>
  );
}
